import * as React from 'react';
import './App.css';
import SharePointLogin from './components/SharePointLogin';
import VerifyExternalUser from './components/VerifyExternalUser';
import AcceptSharePointInvite from './components/AcceptSharePointInvite';
import CompleteAccess from './components/CompleteAccess';
import StepIndicator from './components/StepIndicator';
import { StepIndicatorProps } from './types';

const App: React.FC = () => {
  const [currentStep, setCurrentStep] = React.useState<number>(1);
  const [stepsCompleted, setStepsCompleted] = React.useState<boolean[]>([false, false, false, false]);
  const [processEnded, setProcessEnded] = React.useState<boolean>(false);

  const completeStep = (step: number): void => {
    const newStepsCompleted = [...stepsCompleted];
    newStepsCompleted[step - 1] = true;
    setStepsCompleted(newStepsCompleted);
    
    setCurrentStep(step + 1);
    
    if (step + 1 === 4) {
      setTimeout(() => {
        const finalStepsCompleted = [...newStepsCompleted];
        finalStepsCompleted[3] = true; // Mark the 4th step (index 3) as completed
        setStepsCompleted(finalStepsCompleted);
      }, 500); 
    }
  };

  const handleReject = (): void => {
    // Handle invitation rejection
    setProcessEnded(true);
    setCurrentStep(5); // Move to a state outside the normal flow
  };

  const renderStep = (): React.ReactElement => {
    switch (currentStep) {
      case 1:
        return <SharePointLogin onComplete={() => completeStep(1)} />;
      case 2:
        return <VerifyExternalUser onComplete={() => completeStep(2)} />;
      case 3:
        return <AcceptSharePointInvite 
                onComplete={() => completeStep(3)} 
                onReject={handleReject} 
               />;
      case 4:
        return <CompleteAccess />;
      default:
        return (
          <div className="step-content">
            <h2>Process Ended</h2>
            <p>You've declined the KCNSC O365 access invitation.</p>
            <button 
              className="primary-button"
              onClick={() => {
                // Reset the process
                setCurrentStep(1);
                setStepsCompleted([false, false, false, false]);
                setProcessEnded(false);
              }}
            >
              Start Over
            </button>
          </div>
        );
    }
  };

  // Define the step indicators with their props
  const stepIndicators: StepIndicatorProps[] = [
    {
      step: 1,
      label: "Login",
      isActive: currentStep === 1,
      isCompleted: stepsCompleted[0]
    },
    {
      step: 2,
      label: "Verify User",
      isActive: currentStep === 2,
      isCompleted: stepsCompleted[1]
    },
    {
      step: 3,
      label: "Accept Invite",
      isActive: currentStep === 3,
      isCompleted: stepsCompleted[2]
    },
    {
      step: 4,
      label: "Complete",
      isActive: currentStep === 4,
      isCompleted: stepsCompleted[3]
    }
  ];

  return (
    <div className="app-container">
      <div className="process-container">
        <h1 className="app-title">KSNSC O365 External Access</h1>
        
        {/* Only show step indicators when the process is not ended */}
        {!processEnded && (
          <div className="step-indicators">
            {stepIndicators.map((props, index) => (
              <StepIndicator
                key={index}
                step={props.step}
                label={props.label}
                isActive={props.isActive}
                isCompleted={props.isCompleted}
              />
            ))}
          </div>
        )}
        
        <div className="content-box">
          {renderStep()}
        </div>
      </div>
    </div>
  );
};

export default App;